import { useLingui } from "@lingui/react";
import { useLinkProps } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { formatEventDate } from "@/features/Events/utils";
import { EventCard } from "@whoosh/design-system/src/apps/PatronApp/components/EventCard/EventCard";

import { useEventLocationLabel } from "../../hooks/useEventLocationLabel";
import { formatEventTime } from "../../utils";
import {
  ClientEventSignUpAccess,
  ClientEventSignUpStatus,
  TimeListEventsCard_EventSession$key,
} from "./__generated__/TimeListEventsCard_EventSession.graphql";

export const TimeListEventsCardFragmentString = graphql`
  fragment TimeListEventsCard_EventSession on EventSession {
    id
    bookingsPublishedAt
    displayTime
    startAt
    endAt
    agenda {
      date
    }
    event {
      id
      tags {
        edges {
          node {
            name
          }
        }
      }
      clientEligibleSignUpWindows(clientId: $memberId) {
        opensAt
      }
      accessStatusForClient(clientId: $memberId)
      signUpStatusForClient(clientId: $memberId)
      spotsAvailable
      eventSeries {
        color
        name
        facility {
          copyOverrides {
            course {
              plural
            }
          }
          department {
            type
            name
          }
        }
        image {
          # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
          publicId: renderingKey
        }
      }
    }
    ...useEventLocationLabel_Event
  }
`;

type TimeListEventsCardProps = {
  fragmentRef: TimeListEventsCard_EventSession$key | null;
};

export const TimeListEventsCard = ({
  fragmentRef,
}: TimeListEventsCardProps) => {
  const { i18n } = useLingui();
  const eventSession = useFragment(
    TimeListEventsCardFragmentString,
    fragmentRef
  );

  const event = eventSession?.event;
  const tags = event?.tags?.edges?.map((tag) => tag?.node?.name ?? "") || [];

  const date = eventSession?.agenda?.date ?? "";
  const formattedDate = formatEventDate(date, i18n.locale);

  const startTime = eventSession?.startAt || "";
  const endTime = eventSession?.endAt || "";
  const displayTime = eventSession?.displayTime;

  const accessStatusForClient = event?.accessStatusForClient || "";
  const signUpStatusForClient = event?.signUpStatusForClient || "";
  const spotsAvailable = event?.spotsAvailable || 0;

  const departmentType =
    event?.eventSeries?.facility?.department?.type || "GOLF";
  const departmentName = event?.eventSeries?.facility?.department?.name || "";

  const locationLabel = useEventLocationLabel({
    eventSessionRef: eventSession,
  });

  const opensAtWindows = event?.clientEligibleSignUpWindows?.filter((suw) => {
    return !!suw?.opensAt;
  });

  const opensAtWindow =
    !!opensAtWindows?.length && !!opensAtWindows?.[0]?.opensAt
      ? opensAtWindows?.[0]?.opensAt
      : undefined;

  const { onPress } = useLinkProps({
    screen: "EventDetail",
    params: { eventID: event?.id },
  });

  return (
    <EventCard
      subTitle={event?.eventSeries?.name || ""}
      eventTime={formatEventTime({
        displayTime,
        startAt: startTime,
        endAt: endTime,
      })}
      eventColor={event?.eventSeries?.color ?? "A"}
      publicId={event?.eventSeries?.image?.publicId}
      spotsAvailable={spotsAvailable}
      eventStatus={getEventStatus(signUpStatusForClient, accessStatusForClient)}
      opensAt={opensAtWindow}
      onPress={onPress}
      eventDate={formattedDate}
      departmentType={departmentType}
      departmentName={departmentName}
      tags={tags}
      locationLabel={locationLabel}
      eventSessionId={eventSession?.id ?? null}
      pairingsPublished={eventSession?.bookingsPublishedAt != null}
    />
  );
};

const getEventStatus = (
  signupStatus: ClientEventSignUpStatus | "",
  accessStatus: ClientEventSignUpAccess | ""
) => {
  if (signupStatus === "WAITLIST") {
    return "ON_WAITLIST";
  }

  if (signupStatus !== "NONE" && signupStatus !== "CHECKED_IN") {
    return signupStatus || "CLOSED";
  }

  if (
    accessStatus !== "DRAFT" &&
    accessStatus !== "UNAVAILABLE" &&
    accessStatus !== "ALREADY_IN_SERIES"
  ) {
    return accessStatus || "CLOSED";
  }

  return "CLOSED";
};
